/**
 * "Start from git" (components/sandbox/StartFromGit.jsx): reading a repository URL the person pasted, the service name
 * it suggests (`services/<name>`), and checking that name. Pure: no network, nothing logged.
 *
 * What vfs-service accepts (src/git/authedGit.js, src/git/repoUrl.js): https:// URLs, or the SSH form a forge's
 * "Clone" button shows (`git@host:group/repo.git`, `ssh://git@host/group/repo.git`), which it clones over HTTPS.
 * A URL with credentials in it is refused here already: tokens come from the person's connected accounts, never from
 * a URL.
 */

export const MAX_SERVICE_NAME_LENGTH = 50;
const SERVICE_NAME = /^[a-z0-9][a-z0-9._-]*$/;
const SCP_LIKE = /^(?:([^@\s/:]+)@)?([A-Za-z0-9.-]+):(?!\/\/)([^\s:][^\s]*)$/;

/**
 * `{ host, path, ssh }` for a repository URL, or `{ error }` (in words). `host` is lower case without the port.
 */
export function parseRepoUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) { return { error: 'Paste the repository’s URL.' }; }
  if (/\s/.test(value)) { return { error: 'A repository URL has no spaces.' }; }
  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(value);
  if (!scheme) {
    const scp = SCP_LIKE.exec(value);
    if (!scp) { return { error: 'Use the https:// address of the repository (or the git@host:group/repo form).' }; }
    return pathResult(scp[2], scp[3], true);
  }
  const kind = scheme[1].toLowerCase();
  const ssh = ['ssh', 'git+ssh', 'ssh+git'].includes(kind);
  if (!ssh && kind !== 'https' && kind !== 'http') { return { error: `Only https:// repositories can be cloned (not ${kind}://).` }; }
  let url;
  try {
    url = new URL(ssh ? `https://${value.slice(scheme[0].length)}` : value);
  } catch {
    return { error: 'This URL could not be read.' };
  }
  if (url.password || (!ssh && url.username)) {
    return { error: 'Do not put credentials in the URL: connect an account in Features › Connections instead.' };
  }
  if (url.search || url.hash) { return { error: 'Use the repository’s address without ? or #.' }; }
  return pathResult(url.hostname, url.pathname, ssh);
}

function pathResult(host, rawPath, ssh) {
  const path = String(rawPath || '').replace(/^\/+|\/+$/g, '');
  if (!host || !path || path.includes('..')) { return { error: 'This URL has no repository path (expected host/group/repo).' }; }
  return { host: host.toLowerCase(), path, ssh };
}

/** A name usable as a folder under services/: lower case, letters, digits, '.', '_' and '-'; '' when nothing is left. */
export function slugServiceName(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, MAX_SERVICE_NAME_LENGTH)
    .replace(/[._-]+$/g, '');
}

/** The service name a repository suggests: its last path part without `.git` ("https://github.com/acme/Shop-API.git" → "shop-api"). */
export function repoNameFromUrl(raw) {
  const parsed = parseRepoUrl(raw);
  if (parsed.error) { return ''; }
  let last = parsed.path.split('/').filter(Boolean).pop() || '';
  try { last = decodeURIComponent(last); } catch { /* kept as it is */ }
  return slugServiceName(last.replace(/\.git$/i, ''));
}

/** `base`, else `base-2`, `base-3`, … : the first that is not in `taken`. */
export function uniqueServiceName(base, taken = []) {
  const used = new Set(taken.map((name) => String(name).toLowerCase()));
  const start = slugServiceName(base) || 'repo';
  if (!used.has(start)) { return start; }
  for (let n = 2; ; n += 1) {
    const suffix = `-${n}`;
    const candidate = `${start.slice(0, MAX_SERVICE_NAME_LENGTH - suffix.length)}${suffix}`;
    if (!used.has(candidate)) { return candidate; }
  }
}

/**
 * Why `name` cannot be a new service (folder `services/<name>`), or null. `taken`: the names already used. `folder`:
 * the parent folder the message names (`context` for a context repo; same rule).
 */
export function validateServiceName(raw, taken = [], folder = 'services') {
  const name = String(raw || '').trim();
  if (!name) { return 'Give the service a name.'; }
  if (name.length > MAX_SERVICE_NAME_LENGTH) { return `Keep it to ${MAX_SERVICE_NAME_LENGTH} characters or fewer.`; }
  if (!SERVICE_NAME.test(name)) { return 'Use lower-case letters, digits, “-”, “_” or “.”, starting with a letter or digit.'; }
  if (taken.some((other) => String(other).toLowerCase() === name.toLowerCase())) { return `${folder}/${name} is already used: pick another name.`; }
  return null;
}

/** The person's git accounts for `host` (from GET /api/v1/me/accounts), the default one first. */
export function accountsForHost(accounts, host) {
  const wanted = String(host || '').toLowerCase();
  if (!wanted) { return []; }
  return (accounts || [])
    .filter((account) => String(account?.host || '').toLowerCase() === wanted)
    .sort((a, b) => Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault)));
}

/** A service key of the project configuration ("services/web" or "web") as its short name. */
export const shortServiceName = (key) => String(key || '').replace(/^services\//, '');
